import { Button } from '@components/Button';
import { Icon, IconProp } from '@components/Icon';
import { t } from '@lingui/core/macro';
// eslint-disable-next-line no-restricted-imports
import { newColors } from '@components/Theme/colors';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { FS_UNMASK } from '@utils/fullstory';
import { HTMLProps, PropsWithChildren } from 'react';
import {
  AlertTypeProps,
  BUTTONS_BY_TYPE,
  BUTTONS_BY_TYPE_INVERSE,
  ICONS_BY_TYPE,
  SystemAlertType,
  getTitleByType,
} from './constants';

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: max-content;
`;

const Title = styled.div<AlertTypeProps>((props) => ({
  margin: '0 0 0 8px',
  color: props?.color ? props.color : newColors.system[props.type].title,
  textAlign: 'center',
  fontSize: 14,
  fontWeight: '700',
}));

interface HeaderProps {
  type: SystemAlertType;
  title?: string;
  unmaskTitle?: boolean;
  colors?: {
    accent: string;
    title: string;
  };
}

export const SystemAlertHeader = ({
  type,
  title,
  unmaskTitle,
  colors,
}: HeaderProps): JSX.Element => {
  const { system } = useTheme();
  const accent = colors?.accent ?? system[type].accent;
  const titleColor = colors?.title ?? system[type].title;
  return (
    <Header>
      <Icon
        name="info"
        i={ICONS_BY_TYPE[type] as IconProp}
        color={accent as fixMe}
        size="md"
      />
      <Title
        type={type}
        className={unmaskTitle ? FS_UNMASK : undefined}
        color={titleColor}
      >
        {title ? title : getTitleByType(type)}
      </Title>
    </Header>
  );
};

interface DismissButtonProps extends HTMLProps<HTMLButtonElement> {
  type: SystemAlertType;
  onClick: () => void;
  style?: React.CSSProperties;
  dismiss?: {
    label?: string;
    onClick?: () => void;
  };
}

export const AlertDismissButton = ({
  type,
  onClick,
  style,
  dismiss,
  className,
}: DismissButtonProps): JSX.Element => {
  const handleClick = (): void => {
    dismiss?.onClick && dismiss.onClick();
    onClick();
  };
  return (
    <Button
      className={[FS_UNMASK, className].filter(Boolean).join(' ')}
      option={BUTTONS_BY_TYPE_INVERSE[type]}
      onClick={handleClick}
      style={{ fontWeight: 500, ...buttonWidthStyle, ...style }}
      aria-label={t`Dismiss Notification`}
      name="dismiss-notification-button"
    >
      {dismiss?.label ? dismiss.label : t`Dismiss`}
    </Button>
  );
};

const buttonWidthStyle = {
  maxWidth: 114,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const StyledButton = styled(Button)({
  ...buttonWidthStyle,
  marginRight: 12,
  fontWeight: 500,
  ':hover': {
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
  },
});

interface AlertActionButtonProps {
  type: SystemAlertType;
  action: {
    label: string;
    onClick: () => void;
  };
  style?: React.CSSProperties;
  className?: string;
  closeToast?: () => void;
}

export const AlertActionButton: React.FC<
  PropsWithChildren<AlertActionButtonProps>
> = ({ type, action, style, className, closeToast }) => {
  const handleClick = (): void => {
    if (closeToast) {
      action.onClick();
      closeToast();
    } else {
      action.onClick();
    }
  };

  return (
    <StyledButton
      name=""
      className={[FS_UNMASK, className].filter(Boolean).join(' ')}
      option={BUTTONS_BY_TYPE[type]}
      onClick={handleClick}
      style={style}
      data-testid="alert-action-btn"
    >
      {action.label}
    </StyledButton>
  );
};
